/**
 * The library's small vocabulary of surfaces.
 *
 * Deliberately NOT the Django admin's: this page is a reading surface
 * for people reviewing other teams' work, and the admin's table chrome
 * asks them to hunt.  What it borrows from the admin is the permission
 * and nothing else.
 *
 * Every colour is a `theme.css` token, so the page follows the viewer's
 * light/dark preference without a second palette to keep in step — and
 * the token suite (`apps/coaching/tests/themeTokens.test.ts`) fails the
 * build if one of these names does not exist.
 */

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

export const MONO =
  "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

export type Tone = "neutral" | "good" | "warn" | "bad" | "accent";

const TONES: Record<Tone, { bg: string; border: string; text: string }> = {
  neutral: {
    bg: "var(--color-bg-raised)",
    border: "var(--color-border)",
    text: "var(--color-text-secondary)",
  },
  good: {
    bg: "var(--color-success-bg)",
    border: "var(--color-success-border)",
    text: "var(--color-success-text)",
  },
  warn: {
    bg: "var(--color-warning-bg)",
    border: "var(--color-warning-border)",
    text: "var(--color-warning-text)",
  },
  bad: {
    bg: "transparent",
    border: "var(--color-error)",
    text: "var(--color-error)",
  },
  accent: {
    bg: "var(--color-primary-muted)",
    border: "var(--color-primary-border)",
    text: "var(--color-primary)",
  },
};

/** A small status word: a verdict, a defect, a count that means something. */
export function Badge({
  tone = "neutral",
  mono,
  title,
  testId,
  children,
}: {
  tone?: Tone;
  mono?: boolean;
  title?: string;
  testId?: string;
  children: ReactNode;
}) {
  const palette = TONES[tone];
  return (
    <span
      title={title}
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 7px",
        borderRadius: 6,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.text,
        fontFamily: mono ? MONO : undefined,
        fontSize: 11,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** A filter chip: a toggle that carries its own count. */
export function Chip({
  active,
  count,
  onClick,
  disabled,
  testId,
  children,
}: {
  active: boolean;
  count?: number;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  const empty = count === 0;
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 30,
        padding: "4px 10px",
        borderRadius: 999,
        cursor: disabled ? "default" : "pointer",
        border: `1px solid ${active ? "var(--color-primary-border)" : "var(--color-border)"}`,
        background: active ? "var(--color-primary-muted)" : "transparent",
        color: active
          ? "var(--color-primary)"
          : empty
            ? "var(--color-text-muted)"
            : "var(--color-text-secondary)",
        fontSize: 12,
        fontFamily: "inherit",
        opacity: empty && !active ? 0.55 : 1,
        transition: "background 120ms ease, border-color 120ms ease, color 120ms ease",
      }}
    >
      {children}
      {count !== undefined ? (
        <span style={{ fontFamily: MONO, fontSize: 11, opacity: 0.75 }}>{count}</span>
      ) : null}
    </button>
  );
}

/**
 * A level as three dots.
 *
 * A number would be shorter and a colour would be prettier; neither
 * survives a reviewer scanning five hundred rows for the hard ones.
 * Filled dots read as a quantity at a glance and stay legible to a
 * reader who cannot tell the colours apart.
 */
export function LevelMeter({ level }: { level: number }) {
  return (
    <span
      title={`level ${level}`}
      aria-label={`level ${level}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
    >
      {[1, 2, 3].map((step) => (
        <span
          key={step}
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: step <= level ? "var(--color-primary)" : "transparent",
            border: `1px solid ${step <= level ? "var(--color-primary)" : "var(--color-border-strong)"}`,
          }}
        />
      ))}
      <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--color-text-muted)" }}>
        L{level || "?"}
      </span>
    </span>
  );
}

/**
 * A SET of levels as three dots — the unit grain's level.
 *
 * A unit is not an instrument, so it has no single level: a bench section
 * holds level 1 reps beside a level 3 capstone, and the honest answer
 * is which rungs are in there rather than an average nobody authored.
 * Each dot is filled when that level is present, so "L1 and L3, nothing
 * in between" reads at a glance — which a number could not say at all.
 */
export function LevelSpread({ levels }: { levels: number[] }) {
  const held = new Set(levels);
  // Numerically, not the default lexical sort: rungs are numbers, and a
  // ladder printed 1/10/2 is the kind of small lie that survives for
  // years because three is as high as this one currently goes.
  const rungs = [...held].sort((a, b) => a - b);
  const label = rungs.length ? `L${rungs.join("/")}` : "—";
  return (
    <span
      title={rungs.length ? `levels ${rungs.join(", ")}` : "embeds no priceable instrument"}
      aria-label={rungs.length ? `levels ${rungs.join(", ")}` : "no level"}
      style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
    >
      {[1, 2, 3].map((step) => (
        <span
          key={step}
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: held.has(step) ? "var(--color-primary)" : "transparent",
            border: `1px solid ${held.has(step) ? "var(--color-primary)" : "var(--color-border-strong)"}`,
          }}
        />
      ))}
      <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--color-text-muted)" }}>
        {label}
      </span>
    </span>
  );
}

/** A small-caps section heading. */
export function Label({
  children,
  style,
  title,
}: {
  children: ReactNode;
  style?: CSSProperties;
  /** What the heading MEANS, for the two words on this page that need it. */
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontSize: 10.5,
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        color: "var(--color-text-muted)",
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** An elevated panel — the page's one container shape. */
export function Panel({
  children,
  style,
  testId,
}: {
  children: ReactNode;
  style?: CSSProperties;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 12,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** A monospaced identifier — ids are for matching, not for reading. */
export function Id({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{ fontFamily: MONO, fontSize: 12.5, wordBreak: "break-all", ...style }}>
      {children}
    </span>
  );
}

/** One `key: value` line in a detail panel. */
export function Field({
  name,
  title,
  children,
}: {
  name: string;
  /** What the field MEANS — the any-of rollups need saying out loud. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(112px, 34%) 1fr", gap: 12, padding: "5px 0" }}>
      <Label style={{ paddingTop: 2 }} title={title}>
        {name}
      </Label>
      <div style={{ fontSize: 13, color: "var(--color-text)", minWidth: 0, wordBreak: "break-word" }}>
        {children}
      </div>
    </div>
  );
}

/** The page's primary button. */
export function Button({
  onClick,
  kind = "quiet",
  disabled,
  testId,
  title,
  children,
}: {
  onClick: () => void;
  kind?: "primary" | "quiet";
  disabled?: boolean;
  testId?: string;
  /** Why it is disabled, mostly — a dead control owes an explanation. */
  title?: string;
  children: ReactNode;
}) {
  const primary = kind === "primary";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        minHeight: 34,
        padding: "6px 14px",
        borderRadius: 8,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: `1px solid ${primary ? "var(--color-primary)" : "var(--color-border)"}`,
        background: primary ? "var(--color-primary)" : "transparent",
        color: primary ? "var(--color-on-primary)" : "var(--color-text-secondary)",
        fontSize: 13,
        fontFamily: "inherit",
        fontWeight: primary ? 600 : 500,
      }}
    >
      {children}
    </button>
  );
}

/** The empty state — says what is missing, never just "no results". */
export function Nothing({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="library-empty"
      style={{
        padding: "48px 24px",
        textAlign: "center",
        color: "var(--color-text-muted)",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}


/** A checkbox with a hit area a finger can find. */
export function Check({
  checked,
  onChange,
  label,
  testId,
  indeterminate,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  testId?: string;
  indeterminate?: boolean;
}) {
  const box = useRef<HTMLInputElement>(null);
  // `indeterminate` has no HTML attribute — it is a DOM property only,
  // so a "some of these are selected" tri-state has to be set here or
  // it silently renders as plain unchecked.
  useEffect(() => {
    if (box.current) box.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={box}
      type="checkbox"
      data-testid={testId}
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      onClick={(event) => event.stopPropagation()}
      style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0 }}
    />
  );
}

/**
 * A dropdown anchored to its trigger.
 *
 * Closes on outside click and on Escape — both, because a menu that
 * only closed one way is a menu that gets left open behind whatever the
 * reader does next.
 */
export function Menu({
  label,
  testId,
  align = "left",
  kind = "quiet",
  children,
}: {
  label: ReactNode;
  testId?: string;
  align?: "left" | "right";
  kind?: "primary" | "quiet";
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (host.current && !host.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={host} style={{ position: "relative", display: "inline-block" }}>
      <Button testId={testId} kind={kind} onClick={() => setOpen((was) => !was)}>
        {label}
      </Button>
      {open ? (
        <div
          role="menu"
          data-testid={testId ? `${testId}-menu` : undefined}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            [align]: 0,
            zIndex: 40,
            minWidth: 220,
            padding: 6,
            borderRadius: 10,
            border: "1px solid var(--color-border)",
            background: "var(--color-bg-elevated)",
            boxShadow: `0 10px 30px var(--color-shadow-strong)`,
          }}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

/** One row in a `Menu`. */
export function MenuItem({
  onClick,
  disabled,
  testId,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        minHeight: 34,
        padding: "7px 10px",
        borderRadius: 7,
        border: "none",
        background: "transparent",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: "var(--color-text)",
        fontFamily: "inherit",
        fontSize: 13,
      }}
    >
      {children}
    </button>
  );
}

/**
 * A modal for a decision that cannot be taken back.
 *
 * Deliberately not dismissible by clicking the backdrop: the dialogs
 * this page opens ask whether to destroy a learner's history, and a
 * stray click should never be able to answer that — even by cancelling,
 * because a reader who thinks they dismissed it and did not is the
 * worse half of the same confusion.
 */
export function Modal({
  title,
  onClose,
  testId,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  testId?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "var(--color-backdrop)",
      }}
    >
      <Panel
        style={{
          width: "min(640px, 100%)",
          maxHeight: "min(80vh, 720px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: `0 24px 60px var(--color-shadow-strong)`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{title}</div>
          <button
            type="button"
            data-testid={testId ? `${testId}-close` : undefined}
            aria-label="close"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              border: "1px solid var(--color-border)",
              background: "transparent",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 16, overflow: "auto", flex: 1 }}>{children}</div>
        {footer ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              padding: "12px 16px",
              borderTop: "1px solid var(--color-border-subtle)",
              flexWrap: "wrap",
            }}
          >
            {footer}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/** The review verdict, as the badge every surface draws it with. */
export function ReviewBadge({ state }: { state: string }) {
  if (state === "reviewed") return <Badge tone="good">reviewed</Badge>;
  if (state === "flagged") return <Badge tone="warn">flagged</Badge>;
  return null;
}
